import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from 'sonner';
import { Radar, History, FileText, Search, Kanban } from 'lucide-react';
import LeadSearchBar from '../components/leads/LeadSearchBar';
import LeadsTable from '../components/leads/LeadsTable';
import LeadDrawer from '../components/leads/LeadDrawer';
import PipelineBoard from '../components/leads/PipelineBoard';
import JobsPanel from '../components/leads/JobsPanel';
import ProposalsPanel from '../components/leads/ProposalsPanel';
import ProposalEditor from '../components/leads/ProposalEditor';
import { DeepAnalysisDialog } from '../components/leads/DeepAnalysisDialog';

export default function LeadsPage() {
  const { workspaceId } = useParams();
  const { api } = useAuth();
  const { isDarkMode } = useTheme();
  const [tab, setTab] = useState('search');
  const [settings, setSettings] = useState(null);
  const [leadsData, setLeadsData] = useState({ leads: [], total: 0, cities: [], industries: [] });
  const [filters, setFilters] = useState({ status: '', city: '', industry: '', q: '', flag: '', favorite: '', pkg: '', sort: 'created_at', page: 1 });
  const [jobs, setJobs] = useState([]);
  const [drawerLeadId, setDrawerLeadId] = useState(null);
  const [editorProposal, setEditorProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState([]);
  const [packageReq, setPackageReq] = useState({ ids: [], skipped: 0 });
  const loadRuns = useCallback(async () => {
    try { const r = await api.get(`/api/workspaces/${workspaceId}/lead-runs`); setRuns(r.data.runs); }
    catch { /* History refresh remains available on transient failure. */ }
  }, [api, workspaceId]);
  useEffect(() => { loadRuns(); const t = setInterval(loadRuns, 6000); return () => clearInterval(t); }, [loadRuns]);
  useEffect(() => {
    const request = e => setPackageReq({ ids: e.detail.lead_ids || [], skipped: e.detail.skipped || 0 });
    window.addEventListener('voxinity:lead-package-request', request);
    return () => window.removeEventListener('voxinity:lead-package-request', request);
  }, []);

  const cardBg = isDarkMode ? 'bg-white/[0.03]' : 'bg-white';
  const cardBorder = isDarkMode ? 'border-white/10' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inputBg = isDarkMode ? 'bg-white/5' : 'bg-gray-50';
  const theme = { isDarkMode, cardBg, cardBorder, textPrimary, textSecondary, inputBg };

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get(`/api/workspaces/${workspaceId}/leads/settings`);
      setSettings(r.data);
    } catch (e) { /* ignore */ }
  }, [api, workspaceId]);

  const loadLeads = useCallback(async () => {
    try {
      const p = new URLSearchParams({ ...filters, page: String(filters.page), page_size: '50' });
      const r = await api.get(`/api/workspaces/${workspaceId}/leads?${p}`);
      setLeadsData(r.data);
    } catch (e) {
      toast.error('Failed to load leads');
    } finally { setLoading(false); }
  }, [api, workspaceId, filters]);

  const loadJobs = useCallback(async () => {
    try {
      const r = await api.get(`/api/workspaces/${workspaceId}/leads/jobs`);
      setJobs(r.data.jobs || []);
    } catch (e) { /* ignore */ }
  }, [api, workspaceId]);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Poll while any job or analysis is running
  const runningJobs = jobs.filter(j => j.status === 'running');
  const hasRunning = runningJobs.length > 0 ||
    leadsData.leads.some(l => (l.analysis || {}).status === 'running' || ['queued', 'running'].includes(l.package_status));
  useEffect(() => {
    if (!hasRunning) return;
    const t = setInterval(() => { loadJobs(); loadLeads(); }, 5000);
    return () => clearInterval(t);
  }, [hasRunning, loadJobs, loadLeads]);

  const tabs = [
    { id: 'search', label: 'Search', icon: Search },
    { id: 'leads', label: 'Leads', icon: Radar },
    { id: 'proposals', label: 'Proposals', icon: FileText },
    { id: 'pipeline', label: 'Pipeline', icon: Kanban },
    { id: 'jobs', label: 'History', icon: History, badge: runningJobs.length },
  ];

  const selectTab = (id) => { setTab(id); if (id !== 'proposals') setEditorProposal(null); if (id !== 'leads') setDrawerLeadId(null); };

  const openProposal = (p) => {
    setEditorProposal(p && (p.status === 'draft' || p.status === 'sent') ? p : null);
    setTab('proposals');
  };

  return (
    <DashboardLayout>
      <div className="px-4 sm:px-6 md:pr-28 pb-12 max-w-[1580px] mx-auto overflow-x-clip" data-testid="leads-page">

        {/* Mobile tab bar */}
        <div className="md:hidden flex gap-1 overflow-x-auto pt-4 -mx-1 px-1" data-testid="leads-tabs-mobile">
          {tabs.map(t => (
            <button key={t.id} onClick={() => selectTab(t.id)} data-testid={`leads-tab-m-${t.id}`}
              className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-full font-semibold whitespace-nowrap transition-colors ${tab === t.id ? 'bg-teal-400/10 text-teal-400' : textSecondary}`}>
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>

        {/* Right rail, mirrors the AI agent settings rail */}
        <nav className={`hidden md:flex fixed right-0 top-[57px] bottom-0 w-[72px] z-30 flex-col items-center py-3 gap-1 border-l ${cardBorder} ${isDarkMode ? 'bg-[#0b0c11]' : 'bg-white'}`} data-testid="leads-rail">
          {tabs.map(t => (
            <button key={t.id} onClick={() => selectTab(t.id)} title={t.label} data-testid={`leads-tab-${t.id}`}
              className={`relative w-[60px] py-2 rounded-xl flex flex-col items-center gap-0.5 border transition-colors ${
                tab === t.id ? 'bg-teal-400/10 text-teal-400 border-teal-400/30' : `${textSecondary} border-transparent ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}`}>
              <t.icon size={17} />
              <span className="text-[10px] font-medium leading-tight">{t.label}</span>
              {t.badge > 0 && <span className="absolute -top-1 -right-1 text-[9px] min-w-[16px] h-4 px-1 rounded-full bg-teal-400 text-black font-bold flex items-center justify-center">{t.badge}</span>}
            </button>
          ))}
        </nav>

        {tab === 'search' && (
          <div className="relative mb-8 pt-10 sm:pt-14 pb-4" data-testid="leads-hero">
            <div aria-hidden className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: 'linear-gradient(to right, rgba(148,163,184,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,0.08) 1px, transparent 1px)',
                backgroundSize: '38px 38px',
                maskImage: 'radial-gradient(ellipse 62% 75% at 50% 38%, black 20%, transparent 72%)',
                WebkitMaskImage: 'radial-gradient(ellipse 62% 75% at 50% 38%, black 20%, transparent 72%)',
              }} />

            <div className="relative z-10 flex flex-col items-center text-center">
              <span className={`inline-flex max-w-full items-center gap-2 text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.4em] uppercase font-bold mb-7 px-4 py-1.5 rounded-full border backdrop-blur-md ${
                isDarkMode ? 'text-teal-300/90 border-teal-400/20 bg-teal-400/[0.05]' : 'text-teal-600 border-teal-300 bg-teal-50'
              }`}>
                <Radar size={11} /> Leads &amp; Outreach
              </span>
              <h1 className={`max-w-full font-display font-black uppercase tracking-tight leading-[0.92] text-4xl sm:text-6xl lg:text-7xl ${textPrimary}`}
                style={{ textShadow: isDarkMode ? '0 0 80px rgba(45,212,191,0.25)' : 'none' }}
                data-testid="leads-page-title">
                Find The
                <br />
                <span className="bg-gradient-to-r from-teal-300 via-cyan-300 to-violet-400 bg-clip-text text-transparent">Money Leaks</span>
              </h1>
              <p className={`mt-7 text-base sm:text-lg max-w-2xl leading-relaxed ${isDarkMode ? 'text-white/45' : 'text-gray-500'}`}>
                Find prospects. Build business-specific demos. Prepare your next proposal.
              </p>
              <div className="w-full max-w-5xl mt-12">
                <LeadSearchBar
                  theme={theme} settings={settings} workspaceId={workspaceId} api={api}
                  onStart={async (payload) => {
                    await api.post(`/api/workspaces/${workspaceId}/leads/search-jobs`, payload);
                    setTab('leads');
                    window.dispatchEvent(new CustomEvent('voxinity:lead-scan-started'));
                    toast.success('Finding businesses in the background', {
                      description: 'New leads appear here as they are found. Leads are free.',
                    });
                    loadJobs();
                  }}
                  onAddWebsite={async (website) => {
                    const { data } = await api.post(`/api/workspaces/${workspaceId}/leads/add-website`, { website });
                    toast.success(data.created ? 'Added to your leads' : 'Already in your leads');
                    setTab('leads'); await loadLeads(); setDrawerLeadId(data.lead_id);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {tab === 'leads' && (
          <div className="mt-6">
            <LeadsTable
              theme={theme} data={leadsData} filters={filters} setFilters={setFilters}
              loading={loading} workspaceId={workspaceId} api={api}
              onOpenLead={(l) => setDrawerLeadId(l.lead_id)}
              onRefresh={loadLeads}
            />
          </div>
        )}
        {tab === 'jobs' && (
          <div className="mt-6"><JobsPanel theme={theme} jobs={jobs} runs={runs} api={api} workspaceId={workspaceId} onRefresh={() => { loadJobs(); loadRuns(); }} /></div>
        )}
        {tab === 'proposals' && (
          editorProposal ? (
            <ProposalEditor
              theme={theme} api={api} workspaceId={workspaceId} proposal={editorProposal}
              onClose={() => { setEditorProposal(null); loadLeads(); }}
            />
          ) : (
            <div className="mt-6">
              <ProposalsPanel theme={theme} api={api} workspaceId={workspaceId}
                onEdit={(p) => setEditorProposal(p)} />
            </div>
          )
        )}

        {tab === 'pipeline' && (
          <div className="mt-6">
            <PipelineBoard theme={theme} api={api} workspaceId={workspaceId}
              onOpenLead={(id) => setDrawerLeadId(id)} />
          </div>
        )}

        {drawerLeadId && (
          <LeadDrawer
            theme={theme} api={api} workspaceId={workspaceId} leadId={drawerLeadId}
            leads={leadsData.leads} settings={settings}
            onNavigate={setDrawerLeadId}
            onClose={() => {
              setDrawerLeadId(null); loadLeads();
              window.dispatchEvent(new CustomEvent('voxinity:lead-updated'));
            }}
            onOpenProposal={(p) => { setDrawerLeadId(null); openProposal(p); }}
          />
        )}
        <DeepAnalysisDialog open={packageReq.ids.length > 0} leadIds={packageReq.ids} skipped={packageReq.skipped} onClose={() => setPackageReq({ ids: [], skipped: 0 })}
          api={api} workspaceId={workspaceId} dark={isDarkMode}
          onStarted={() => {
            setPackageReq({ ids: [], skipped: 0 }); loadRuns(); loadJobs(); loadLeads();
            window.dispatchEvent(new CustomEvent('voxinity:lead-updated'));
            window.dispatchEvent(new CustomEvent('voxinity:lead-scan-started'));
            toast.success('Deep Analysis & Proposal queued', { description: 'Watch each business in Leads, its slide-out, or History. Only completed businesses are charged.' });
          }} />
      </div>
    </DashboardLayout>
  );
}
